import { Badge, ListItemIcon, ListItemText, Stack, type SxProps } from '@mui/material';
import { type LinkProps, type ActiveLinkOptions } from '@tanstack/react-router';
import { type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { merge } from 'ts-deepmerge';
import { Icon } from '@/components/Icon/Icon';
import { ListItemButtonLink } from '@/router/Link';
import { mergeSx } from '@/utils/mergeSx';

const defaultLinkProps = {
  activeOptions: { exact: false, includeSearch: false },
  activeProps: () => ({ selected: true }),
} as const satisfies ActiveLinkOptions;

export const NavItem = ({
  icon,
  to,
  children,
  params,
  search,
  activeLinkExact,
  badgeContent,
}: {
  icon: LucideIcon;
  to: NonNullable<LinkProps['to']>;
  children: string;
  params?: LinkProps['params'];
  search?: LinkProps['search'];
  activeLinkExact?: boolean;
  badgeContent?: number;
}) => (
  <ListItemButtonLink
    to={to}
    search={search}
    params={params}
    dense={true}
    {...merge(defaultLinkProps, { activeOptions: { exact: activeLinkExact } })}
  >
    <NavItemIcon icon={icon} />
    <ListItemText primary={children} />
    {badgeContent && badgeContent > 0 ? (
      <ListItemIcon sx={{ ml: 'auto' }}>
        <Badge badgeContent={badgeContent} color="primary" max={99} />
      </ListItemIcon>
    ) : null}
  </ListItemButtonLink>
);

export const NavItemIcon = ({ icon }: { icon: LucideIcon }) => (
  <ListItemIcon>
    <Icon icon={icon} size={16} />
  </ListItemIcon>
);

export const Nav = ({
  children,
  'aria-label': ariaLabel,
  sx,
}: {
  children: ReactNode;
  'aria-label': string;
  sx?: SxProps;
}) => (
  <Stack component="nav" aria-label={ariaLabel} sx={mergeSx({ gap: 1 }, sx)}>
    {children}
  </Stack>
);
